import * as React from 'react';
import { createPortal } from 'react-dom';
import { DropdownItemProps, Icon, Input } from 'semantic-ui-react';

export interface SemanticTextTableFilterProps {
    value: string;
    placeholder?: string;
    debounceMs?: number;
    onChange: (value: string) => void;
}

export interface SemanticSelectTableFilterProps {
    value: string;
    options: DropdownItemProps[];
    placeholder?: string;
    onChange: (value: string) => void;
}

const stopPropagation = (event: React.SyntheticEvent<HTMLElement>) => {
    event.stopPropagation();
};

interface ClearFilterButtonProps {
    disabled: boolean;
    onClick: () => void;
}

const stopMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
};

const ClearFilterButton: React.FC<ClearFilterButtonProps> = ({ disabled, onClick }) => {
    return (
        <button
            type="button"
            className="semanticTableFilterInlineClearButton"
            aria-label="Clear filter"
            title="Clear filter"
            disabled={disabled}
            onMouseDown={stopMouseDown}
            onClick={(event) => {
                event.preventDefault();
                stopPropagation(event);
                onClick();
            }}>
            <Icon name="close" />
        </button>
    );
};

export const SemanticTableColumnFilter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <div
            className="semanticTableColumnFilter"
            onClick={stopPropagation}
            onDoubleClick={stopPropagation}
            onKeyDown={stopPropagation}>
            {children}
        </div>
    );
};

export const SemanticTextTableFilter: React.FC<SemanticTextTableFilterProps> = ({
    value,
    placeholder,
    debounceMs = 150,
    onChange,
}) => {
    const [localValue, setLocalValue] = React.useState(value);

    React.useEffect(() => {
        setLocalValue(value);
    }, [value]);

    React.useEffect(() => {
        if (debounceMs <= 0) {
            onChange(localValue);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            onChange(localValue);
        }, debounceMs);

        return () => window.clearTimeout(timeoutId);
    }, [debounceMs, localValue, onChange]);

    return (
        <SemanticTableColumnFilter>
            <div className="semanticTableFilterInputWrapper">
                <Input
                    fluid
                    size="mini"
                    icon="search"
                    iconPosition="left"
                    value={localValue}
                    placeholder={placeholder}
                    onChange={(_event, data) => setLocalValue(data.value)}
                />
                <ClearFilterButton
                    disabled={localValue.length === 0}
                    onClick={() => setLocalValue('')}
                />
            </div>
        </SemanticTableColumnFilter>
    );
};

export const SemanticSelectTableFilter: React.FC<SemanticSelectTableFilterProps> = ({
    value,
    options,
    placeholder,
    onChange,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [menuStyle, setMenuStyle] = React.useState<React.CSSProperties | undefined>(undefined);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);

    const selectedOption = React.useMemo(
        () => options.find((option) => String(option.value ?? '') === value),
        [options, value],
    );

    const updateMenuStyle = React.useCallback(() => {
        if (!triggerRef.current) {
            return;
        }

        const rect = triggerRef.current.getBoundingClientRect();
        setMenuStyle({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
        });
    }, []);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        updateMenuStyle();
    }, [isOpen, updateMenuStyle]);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (target
                && (triggerRef.current?.contains(target) || menuRef.current?.contains(target))) {
                return;
            }

            setIsOpen(false);
        };

        const handleWindowChange = () => {
            updateMenuStyle();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        window.addEventListener('resize', handleWindowChange);
        window.addEventListener('scroll', handleWindowChange, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            window.removeEventListener('resize', handleWindowChange);
            window.removeEventListener('scroll', handleWindowChange, true);
        };
    }, [isOpen, updateMenuStyle]);

    const displayValue = selectedOption?.text ?? placeholder ?? '';

    const handleClear = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        stopPropagation(event);
        setIsOpen(false);
        onChange('');
    };

    const menu = isOpen && menuStyle
        ? createPortal(
            <div
                ref={menuRef}
                className="ui visible vertical menu transition semanticTableFilterPortalMenu"
                style={menuStyle}>
                {options.map((option) => {
                    const optionValue = String(option.value ?? '');
                    const optionText = option.text ?? optionValue;

                    return (
                        <button
                            key={option.key ?? optionValue}
                            type="button"
                            className={`item${optionValue === value ? ' active selected' : ''}`}
                            onMouseDown={stopMouseDown}
                            onClick={(event) => {
                                event.preventDefault();
                                stopPropagation(event);
                                setIsOpen(false);
                                onChange(optionValue);
                            }}>
                            {optionText}
                        </button>
                    );
                })}
            </div>,
            document.body,
        )
        : null;

    return (
        <SemanticTableColumnFilter>
            <button
                ref={triggerRef}
                type="button"
                className={`ui fluid selection dropdown semanticTableSelectFilterTrigger${isOpen ? ' active visible' : ''}`}
                onMouseDown={stopMouseDown}
                onClick={() => setIsOpen((previousState) => !previousState)}>
                <span className={`text${value.length === 0 ? ' default' : ''}`}>{displayValue}</span>
                {value.length > 0
                    ? (
                        <span
                            className="semanticTableSelectFilterIcon"
                            onMouseDown={stopMouseDown}
                            onClick={handleClear}>
                            <Icon name="close" />
                        </span>
                    )
                    : (
                        <Icon name="dropdown" />
                    )}
            </button>
            {menu}
        </SemanticTableColumnFilter>
    );
};
